import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Typeahead } from 'react-bootstrap-typeahead';
import { BiChevronDown } from 'react-icons/bi';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import './SearchableSelect.scss';

// Single-value, type-to-filter replacement for a native <select>, for cramped
// spots like grid cells. The menu is position:fixed so it isn't clipped by
// scroll containers (e.g. .table-responsive), and popper flips/shifts it to
// stay inside the viewport. When a value is selected, hovering shows its
// full label via the input's native title (no tooltip element is mounted on
// hover, so nothing reflows), and nothing shows when empty.
const SearchableSelect = ({
	id, options, value, onChange, placeholder = 'Select', disabled = false, className = '',
}) => {
	const typeaheadRef = useRef(null);
	// Compare as strings, like a native <select> does, so a numeric id and its
	// string form still match (e.g. value loaded from the server vs. options).
	const selected = value === '' || value == null
		? []
		: options.filter((opt) => String(opt.value) === String(value));
	const selectedLabel = selected[0]?.label;

	// Typing over a selection unselects it; if the user then leaves without
	// picking an option, drop the leftover text so the box doesn't look filled
	// while actually holding no value.
	const handleBlur = () => {
		if (selected.length === 0) typeaheadRef.current?.clear();
	};

	return (
		<div className={`SearchableSelect ${className}`}>
			<Typeahead
				id={id}
				ref={typeaheadRef}
				labelKey="label"
				options={options}
				selected={selected}
				placeholder={placeholder}
				disabled={disabled}
				onChange={(items) => onChange(items[0]?.value ?? '')}
				onBlur={handleBlur}
				inputProps={{ className: 'builder-ui-select', title: selectedLabel || undefined }}
				align="left"
				flip
				positionFixed
				clearButton={!disabled}
			>
				{({ isMenuShown }) => (
					<BiChevronDown
						aria-hidden="true"
						className={`SearchableSelect__arrow ${isMenuShown ? 'SearchableSelect__arrow--open' : ''}`}
					/>
				)}
			</Typeahead>
		</div>
	);
};

SearchableSelect.propTypes = {
	id: PropTypes.string.isRequired,
	options: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		label: PropTypes.string,
	})).isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	disabled: PropTypes.bool,
	className: PropTypes.string,
};

export default SearchableSelect;
